'use client'

import React, { FC, ReactElement } from 'react'
import styles from './QrScanner.module.css'
import QRCodeIcon from '@/icons/qr-code'
import Modal from '@/components/modal-v2'
import useWindowSize from '@/utils/hooks/useWindowSize'
import XIcon from '@/icons/x-icon'
import classnames from 'classnames'
import Loading from '@/components/circle-loading'
import { loadQrScanner } from '@/components/qr-scanner/load'

const QrScanner: FC<{ onSuccess: (res: string) => void; onError: () => void }> = (
	props
): ReactElement => {
	const [open, setOpen] = React.useState(false)
	const [ready, setReady] = React.useState(false)
	const [loadError, setLoadError] = React.useState<string | null>(null)
	const [attempt, setAttempt] = React.useState(0)
	const [QrReader, setQrReader] = React.useState<React.ComponentType<any> | null>(null)
	const { isDesktop } = useWindowSize()

	React.useEffect(() => {
		if (!open) return
		let cancelled = false
		setLoadError(null)
		loadQrScanner().then((result) => {
			if (cancelled) return
			if (result.error || !result.Reader) {
				setQrReader(null)
				setLoadError(result.error?.message || 'QR scanner failed to load')
				return
			}
			setQrReader(() => result.Reader as React.ComponentType<any>)
		})
		return () => {
			cancelled = true
		}
	}, [open, attempt])
	// const isDesktop = true

	const style = {
		height: '100%',
		width: '100%',
		objectFit: 'fill',
		aspectRatio: 16 / 9,
		background: 'transparent'
	}

	const handleSuccess = (res: any) => {
		if (!res?.text) {
			return
		}

		setReady(false)
		setOpen(false)
		props.onSuccess(res.text.trim())
	}

	const handleError = () => {
		setReady(false)
		setOpen(false)
		props.onError()
	}

	const handleCancel = () => {
		setReady(false)
		setOpen(false)
	}

	return (
		<>
			<QRCodeIcon className={styles.icon} onClick={() => setOpen(true)} />
			<Modal open={open} onClose={() => {}} bodyClassName={styles.modal}>
				{ready ? (
					<>
						<XIcon className={styles.cancelIcon} onClick={handleCancel} />
						<div className={styles.scanCode}>
							<span className={classnames(styles.scanCodeAngle, styles.scanCodeAngleTop)}></span>
							<span className={classnames(styles.scanCodeAngle, styles.scanCodeAngleBottom)}></span>
						</div>
					</>
				) : loadError ? (
					<div className={styles.loadingContainer}>
						<div className={styles.loadError}>
							<p>QR scanner failed to load.</p>
							<div className={styles.loadErrorActions}>
								<button type="button" onClick={() => setAttempt((n) => n + 1)}>
									Retry
								</button>
								<button type="button" onClick={handleCancel}>
									Cancel
								</button>
							</div>
						</div>
					</div>
				) : (
					<div className={styles.loadingContainer}>
						<Loading className={styles.loading} />
					</div>
				)}
				{open && QrReader && (
					<QrReader
						style={style}
						delay={0}
						onError={handleError}
						onScan={handleSuccess}
						onLoad={() => setReady(true)}
						constraints={
							isDesktop
								? undefined
								: {
										video: {
											facingMode: { exact: `environment` }
										}
								  }
						}
					/>
				)}
			</Modal>
		</>
	)
}

export default QrScanner
